import { createContext, useContext, useEffect, useState } from 'react';
import { DeviceService } from '../service/DeviceService';
import { LocationService } from '../service/LocationService';
import { ProductService } from '../service/ProductService';
import { MessageContext } from './MessageContext';
import { useTranslation } from 'react-i18next';

const DashboardContext = createContext(null);

export const DashboardProvider = ({ children }) => {
    const { showErrorMessage } = useContext(MessageContext);
    const { t } = useTranslation();
    const [locationOptions, setLocationOptions] = useState([]);
    const [cityOptions, setCityOptions] = useState([]);
    const [deviceOptions, setDeviceOptions] = useState([]);
    const [productOptions, setProductOptions] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchAllData = async () => {
            setIsLoading(true);
            try {
                // Pobierz wszystkie dane równolegle
                const [locations, cities, devices, products, categories] = await Promise.all([
                    LocationService.getAllLocations().catch(err => {
                        showErrorMessage(t('dashboard.error.fetchLocations'));
                        return [];
                    }),
                    LocationService.getAllCities().catch(err => {
                        showErrorMessage(t('dashboard.error.fetchCities'));
                        return [];
                    }),
                    DeviceService.getAllDevices().catch(err => {
                        showErrorMessage(t('dashboard.error.fetchDevices'));
                        return [];
                    }),
                    ProductService.getAllProducts().catch(err => {
                        showErrorMessage(t('dashboard.error.fetchProducts'));
                        return [];
                    }),
                    ProductService.getCategoryNames().catch(err => {
                        showErrorMessage(t('dashboard.error.fetchCategories'));
                        return [];
                    })
                ]);
                setLocationOptions(locations.filter(l => l.name).map(l => ({ id: l.id, label: l.name })));
                setCityOptions(cities.map(c => ({ id: c, label: c })));
                setDeviceOptions(devices.map(d => ({ id: d.id, label: d.name })));
                setProductOptions(products.map(p => ({ id: p.id, label: p.name })));
                setCategoryOptions(categories);
            } catch (err) {
                console.error('Error fetching dashboard data:', err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAllData();
    }, []);

    const value = {
        locationOptions,
        cityOptions,
        deviceOptions,
        productOptions,
        categoryOptions,
        isLoading
    };

    return (
        <DashboardContext.Provider value={value}>
            {children}
        </DashboardContext.Provider>
    );
};

export const useDashboardData = () => {
    const context = useContext(DashboardContext);
    if (!context) {
        throw new Error('useDashboardData must be used within DashboardProvider');
    }
    return context;
};

export default DashboardContext;

